import { Card, Form, DatePicker, Button, Select, Row, Col, Table, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { users } from '../../../../../../../API/TT_ELD/Users/user';
import MalfunctionDebugModal from './DebugInfoModal';
import { CalendarOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;

interface Params {
  [key: string]: string | undefined;
  id: string | undefined;
}

const MalfunctionTab = () => {
  const { id } = useParams<Params>();
  const [form] = Form.useForm();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [debugModal, setDebugModal] = useState(false);

  const fetchData = async (currentPage: number) => {
    const values = form.getFieldsValue();
    const [from, to] = values.range || [];

    if (!from || !to) return;

    const queryFrom = dayjs(from).toISOString();
    const queryTo = dayjs(to).toISOString();

    const selectedTypes = values.types || [];
    const isAll = selectedTypes.includes('all');
    const types = isAll ? [] : selectedTypes;

    const body = { types, isAll };

    try {
      setLoading(true);

      const res = await users.PostMalfunctions(id, queryFrom, queryTo, body, currentPage);

      let list: any[] = [];
      let total = 0;

      // data
      if (Array.isArray(res?.data)) list = res.data;
      else if (Array.isArray(res?.data?.data)) list = res.data.data;
      else if (Array.isArray(res?.result)) list = res.result;
      else if (Array.isArray(res)) list = res;

      // count
      total = res?.count ?? res?.data?.count ?? res?.data?.data?.count ?? 0;

      setData(list || []);
      setTotalCount(total);
    } catch {
      message.error('Failed to load malfunction data');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async () => {
    setPage(1);
    fetchData(1);
  };

  const presets: { label: string; value: [Dayjs, Dayjs] }[] = [
    { label: 'Today', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
    {
      label: 'Last 7 Days',
      value: [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')],
    },
    {
      label: 'Last 30 Days',
      value: [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')],
    },
  ];

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_: any, __: any, index: number) => (page - 1) * 10 + (index + 1),
    },
    { title: 'Status', dataIndex: 'status' },
    {
      title: 'Start Time',
      dataIndex: 'start_date',
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'End Time',
      dataIndex: 'end_date',
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
    { title: 'Address', dataIndex: 'address' },
    { title: 'Note', dataIndex: 'note' },
    { title: 'Vehicle ID', dataIndex: 'vehicleId' },
    {
      title: 'Debug Info',
      dataIndex: 'debug_info',
      render: (_: any, record: any) =>
        record?.debug_info ? (
          <Button
            type="link"
            onClick={() => {
              setSelectedItem(record);
              setDebugModal(true);
            }}
          >
            View
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <Card>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={24} md={12} lg={6}>
              <Form.Item
                label="Date range"
                name="range"
                rules={[{ required: true, message: 'Select date range' }]}
              >
                <RangePicker
                  placeholder={['Start date & time', 'End date & time']}
                  showTime={{ format: 'HH:mm' }}
                  suffixIcon={<CalendarOutlined />}
                  presets={presets}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12} lg={6}>
              <Form.Item
                label="Types"
                name="types"
                rules={[{ required: true, message: 'Select at least one type' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select types"
                  showSearch={false}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 10, label: 'Internet Disconnect' },
                    { value: 11, label: 'Internet Connect' },
                    { value: 20, label: 'ELD Disconnect' },
                    { value: 21, label: 'ELD Connect' },
                    { value: 30, label: 'App Paused' },
                    { value: 31, label: 'App worked' },
                    { value: 40, label: 'Battery' },
                    { value: 50, label: 'Bluetooth OFF' },
                    { value: 51, label: 'Bluetooth ON' },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col
              xs={24}
              sm={24}
              md={12}
              lg={4}
              style={{ display: 'flex', alignItems: 'center', marginTop: 5 }}
            >
              <Button type="primary" htmlType="submit">
                Send
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>

      <Table
        style={{ marginTop: 20 }}
        dataSource={data}
        columns={columns}
        loading={loading}
        rowKey={(record) => record.id}
        pagination={{
          current: page,
          showSizeChanger: false,
          total: totalCount,
        }}
        scroll={{ x: 'max-content' }}
        onChange={(pagination) => {
          const newPage = pagination.current || 1;
          setPage(newPage);
          fetchData(newPage);
        }}
      />

      <MalfunctionDebugModal
        data={selectedItem}
        isModalVisible={debugModal}
        setIsModalVisible={setDebugModal}
      />
    </>
  );
};

export default MalfunctionTab;
